/**
 * An "I know the rate" option: a rate the operator typed from memory, priced
 * once, for one quote.
 *
 * The point of this module is that its output is INDISTINGUISHABLE from an
 * option built out of saved carrier records (FinalQuoteCostTable's
 * airline x agent cross-product). Same field names, same nesting. That is what
 * lets the whole downstream — the maths, the row renderer, the customer
 * preview, the export — work on an ad-hoc rate without knowing it is one.
 *
 * Why this exists at all: to price a shipment they already knew the price of,
 * an operator had to create a permanent tenant-wide Airline master with a
 * fabricated email and a globally-unique code, then a SavedAirline, then the
 * same again for a transporter. For a number they could write on a napkin.
 */

/** How long before a typed rate is old enough to warrant a second look. */
export const AD_HOC_STALE_DAYS = 7;

const num = (v) => {
  if (v === null || v === undefined || String(v).trim() === "") return 0;
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

/** A positive finite number, or null if the input cannot be one. */
const positive = (v) => {
  if (v === null || v === undefined || String(v).trim() === "") return null;
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? n : null;
};

/**
 * Stable enough to key a costTableDraft row and to survive a reload, without
 * pulling in a uuid dependency. Not security-sensitive.
 */
function makeKey(carrierName, iso) {
  const slug = String(carrierName || "rate")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 24);
  return `adhoc_${slug}_${Date.parse(iso) || 0}`;
}

/**
 * @returns {{ option: object|null, errors: Array<{field: string, message: string}> }}
 *   `option` is null when anything required is missing — a half-built option
 *   must never reach the table, because a silently-zeroed rate would quote a
 *   customer the wrong price.
 */
export function buildAdHocOption(input = {}, context = {}) {
  const errors = [];

  /* Deliberately OPTIONAL. An earlier version required it, on the reasoning
     that a quotation names who flies the cargo. That is true of the formal,
     structured quotation — but operators also reply in one line ("after
     calculation, £3.50/kg"), where the carrier is irrelevant. Forcing a name
     to reach a number is friction with no payoff, and naming it is the
     operator's call, not ours. */
  const carrierName = String(input.carrierName || "").trim();

  const ratePerKg = positive(input.ratePerKg);
  if (ratePerKg === null) {
    errors.push({
      field: "ratePerKg",
      message: "Enter the air rate per kg.",
    });
  }

  /* The quote's packages give the weight, but an operator pricing from a rate
     they already know often has a figure the container has not caught up with.
     A typed value wins; a blank, zero or negative one falls back rather than
     zeroing the divisor and quoting a per-kg rate of Infinity. */
  const overrideCW = positive(input.chargeableWeight);
  const CW = overrideCW !== null ? overrideCW : num(context.cumulativeVolumeWeight);
  if (CW <= 0) {
    errors.push({
      field: "cumulativeVolumeWeight",
      message:
        "This quote has no chargeable weight yet — add package dimensions first.",
    });
  }

  if (errors.length) return { option: null, errors };

  const now = context.now instanceof Date ? context.now : new Date();
  const enteredAt = now.toISOString();

  const screeningAmount = num(input.screeningAmount);
  const screeningType = input.screeningType === "flat" ? "flat" : "perKg";

  const option = {
    key: makeKey(carrierName, enteredAt),
    isAdHoc: true,
    enteredAt,
    enteredBy: context.enteredBy || null,

    airline: {
      name: carrierName,
      // No invented placeholder: an empty name must stay empty so nothing
      // downstream prints a fake carrier on a customer's quotation.
      airlineCode: carrierName ? carrierName.slice(0, 3).toUpperCase() : "",
      // Deliberately null: an ad-hoc rate references no directory record, and
      // nothing downstream may treat it as though it does.
      savedAirlineId: null,
      airlineId: null,
      transitTime: input.transitTime || "N/A",
      ratePerVolumeWeight: ratePerKg,
      currency: input.currency || "GBP",
      rateValidityUntil: input.rateValidityUntil || null,
      // Empty by design — ad-hoc options are exempt from the origin-airport
      // matching gate, because the operator typed both sides themselves.
      originAirports: [],
      /* The EXACT shape FinalQuoteCostTable reads (see its lines 838-851):
         it takes the rate from `primary.ratePerVolumeWeight ?? primary` and the
         MINIMUM from `primary.minCharge`. Storing the min as a sibling looked
         fine in this lane and silently dropped it the moment the option reached
         the cost table — a £50 screening minimum priced here and vanished
         there. Nothing renders these objects directly, so the object form is
         safe; the earlier "must be a number" belief came from a different
         field (FFWH.fuelSurcharge) that genuinely was rendered raw. */
      screeningPrices: {
        primary: {
          ratePerVolumeWeight: num(input.airlinePrimaryScreening),
          minCharge: num(input.airlinePrimaryScreeningMin),
        },
        secondary: {
          ratePerVolumeWeight: num(input.airlineSecondaryScreening),
          minCharge: num(input.airlineSecondaryScreeningMin),
        },
      },
      FFWH: {
        freightCharge: { minCharge: num(input.minCharge) },
        fuelSurcharge: { ratePerVolumeWeight: num(input.fuelPct) },
        warRiskSurcharge: {
          ratePerVolumeWeight: num(input.warRiskPerKg),
          minCharge: num(input.warRiskMin),
        },
        handlingFee: {
          ratePerVolumeWeight: num(input.handlingPerKg),
          minCharge: num(input.handlingMin),
        },
      },
    },

    origin: context.origin || "N/A",
    destination: context.destination || "N/A",
    serviceScope: context.serviceScope || "",
    cumulativeVolumeWeight: CW,

    // Optional. An airport-to-airport shipment has no collection leg, and 0 is
    // a legitimate answer rather than a missing one.
    transportCost: num(input.transportCost),
    transporterName: String(input.transporterName || "").trim() || null,
    selectedTransporterId: null,
    agentType: "ad-hoc",
    agentCode: "ADHOC",

    /* Transport-side screening (PTS/STS). The airline side lives on
       airline.screeningPrices above — the table reads the two separately, and
       the Charges tabs collect both, so the lane must too. */
    screeningPrices: [
      {
        selectBox: false,
        destinationAirport: "any",
        screeningType,
        /* Which single screening the operator says applies — PAS, SAS, PTS or
           STS, or none. The cost table charges exactly one; so does this. */
        screeningApplies: String(input.screeningApplies || "").toUpperCase(),
        primary: num(input.transportPrimaryScreening ?? screeningAmount),
        secondary: num(input.transportSecondaryScreening),
        /* Same reason as above — the cost table reads transport minimums from
           `sp.primaryScreeningPrice.minCharge` (its lines 821-835). */
        primaryScreeningPrice: {
          ratePerVolumeWeight: 0,
          minCharge: num(input.transportPrimaryScreeningMin),
        },
        secondaryScreeningPrice: {
          ratePerVolumeWeight: 0,
          minCharge: num(input.transportSecondaryScreeningMin),
        },
        profitMargin: num(input.profitMargin),
        extra: num(input.extra),
        custom: num(input.customs),
      },
    ],

    profitMargin: num(input.profitMargin),
    custom: num(input.customs),
    extra: num(input.extra),
    dataForCalculation: {},
  };

  return { option, errors: [] };
}

/**
 * Should the origin-airport matching gate be applied to this option?
 *
 * For a saved carrier the gate is meaningful: the airline record's origin
 * airports and the transporter record's departure airport are two independent
 * pieces of directory data, and a mismatch means the pairing is nonsense.
 *
 * An ad-hoc option has no records to reconcile — the operator typed both sides
 * themselves. Applying the gate there can only make their own rate silently
 * vanish from the table, which is the exact failure this feature exists to end.
 */
export function shouldApplyOriginAirportGate(option, isFinalizedQuote) {
  if (isFinalizedQuote) return false;
  return !option?.isAdHoc;
}

/**
 * Fold stored ad-hoc options into the saved cross-product.
 *
 * They become their own group, appended after the saved ones, so the table's
 * existing group-per-transport structure is preserved and a quote with NO saved
 * carriers still renders rows.
 */
export function mergeAdHocOptions(savedGroups = [], storedAdHoc, context = {}) {
  const stored = Array.isArray(storedAdHoc) ? storedAdHoc : [];
  if (!stored.length) return savedGroups;

  const built = stored
    .map(row => {
      /* A quote-sheet option prices in NAMED CHARGE LINES and belongs to
         QuoteSheetCostTable, which renders those names. Folding it in here as
         well would give one figure two owners — this table offers its own
         screening, customs, margin and extra fields on top of money the sheet
         has already accounted for. */
      if (Array.isArray(row.lines) && row.lines.length) return null;
      const { option } = buildAdHocOption(
        {
          carrierName: row.carrierName,
          transporterName: row.transporterName,
          ratePerKg: row.ratePerVolumeWeight,
          minCharge: row.minCharge,
          fuelPct: row.fuelPct,
          warRiskPerKg: row.warRiskPerKg,
          warRiskMin: row.warRiskMin,
          handlingPerKg: row.handlingPerKg,
          handlingMin: row.handlingMin,
          transitTime: row.transitTime,
          airlinePrimaryScreening: row.airlinePrimaryScreening,
          airlinePrimaryScreeningMin: row.airlinePrimaryScreeningMin,
          airlineSecondaryScreening: row.airlineSecondaryScreening,
          airlineSecondaryScreeningMin: row.airlineSecondaryScreeningMin,
          transportPrimaryScreening: row.transportPrimaryScreening,
          transportPrimaryScreeningMin: row.transportPrimaryScreeningMin,
          transportSecondaryScreening: row.transportSecondaryScreening,
          transportSecondaryScreeningMin: row.transportSecondaryScreeningMin,
          customs: row.customs,
          transportCost: row.transportCost,
          screeningType: row.screeningType,
          screeningAmount: row.screeningAmount,
          currency: row.currency,
          profitMargin: row.profitMargin,
          extra: row.extra,
        },
        context
      );
      if (!option) return null;
      // Keep the STORED identity and timestamp. Rebuilding them on every load
      // would reset the staleness clock, so a months-old rate would read as
      // typed just now, and draft rows keyed to the old id would stop matching.
      return {
        ...option,
        key: row.key || option.key,
        enteredAt: row.enteredAt || option.enteredAt,
        enteredBy: row.enteredBy || option.enteredBy,
      };
    })
    .filter(Boolean);

  if (!built.length) return savedGroups;
  return [...savedGroups, built];
}

/**
 * Is a typed rate old enough that the operator should re-check it before
 * quoting from it?
 *
 * There is no saved record to recompute from — the operator IS the source — so
 * staleness is answered by disclosure rather than by refreshing. An unparseable
 * date returns false: a false warning on a quote is worse than no warning.
 */
export function isAdHocRateStale(
  enteredAt,
  now = new Date(),
  days = AD_HOC_STALE_DAYS
) {
  const then = Date.parse(enteredAt);
  if (!Number.isFinite(then)) return false;
  return now.getTime() - then > days * 24 * 60 * 60 * 1000;
}
